import type { FilterGroup, FilterGroupLeaf, RelationshipFilter, UnifiedFilters } from 'types/filters';
import { isMeasureRelationship } from '../filterGroupLeaf';
import { isFilterVariable } from '../filterVariables';

/**
 * OQ-7 (specs/unified-filters-v2.md) — closing the measure-enrollment
 * permission-inference channel: a `measures_into` relationship leaf names an
 * aggregate sphere directly (`relatedTo: [aggregateId]`), so saving a filter
 * with that leaf lets an author probe for the existence of an id they cannot
 * otherwise see (the save either succeeds or is rejected, and either answer
 * leaks whether the id exists). Every filter-save path that can carry a
 * `measures_into` leaf — context level/segment/applicability, view
 * `levelFilters`, factor `monitoringRules`, column-filter promote-to-view/
 * context — must call this helper and reject the save on any inaccessible id.
 *
 * Mirrors `applyAccessReadFilter`'s callback-injection shape: this package
 * never imports `@center-platform/permissions` directly (permission
 * enforcement is SpiceDB's job, not this package's — see filterEvaluation.ts),
 * so the actual bulk-permission check is supplied by the caller. Fail-closed:
 * a `checkViewPermission` that throws, or that returns fewer ids than were
 * asked about, is treated as "not accessible" — never as "allowed by error".
 */
export interface RelatedSphereAccessResult {
  valid: boolean;
}

/**
 * Every `measures_into` relationship's `relatedTo` ids, from the top-level array
 * and every nested group. Filter-variable tokens (`@me`, `@parent`, …) are not
 * sphere ids — they resolve at query time to spheres relative to the viewer —
 * so they are never looked up.
 */
function collectMeasureRelatedIds(filters: UnifiedFilters | undefined): string[] {
  if (!filters) return [];
  const ids = new Set<string>();

  const addFromRelationship = (rel: RelationshipFilter) => {
    if (rel.relationshipSystemName !== 'measures_into') return;
    (rel.relatedTo ?? []).forEach((id) => {
      if (!isFilterVariable(id)) ids.add(id);
    });
  };

  (filters.relationshipFilters ?? []).forEach(addFromRelationship);

  const walkLeaf = (leaf: FilterGroupLeaf) => {
    if (isMeasureRelationship(leaf)) {
      addFromRelationship(leaf.filter);
    }
  };

  const walkGroup = (group: FilterGroup) => {
    (group.expressions ?? []).forEach(walkLeaf);
    (group.groups ?? []).forEach(walkGroup);
  };

  (filters.filterGroups ?? []).forEach(walkGroup);

  return [...ids];
}

/**
 * Validate that every sphere id named by a `measures_into` relationship leaf
 * in `filters` is one the author may `view`. Returns `{ valid: false }` on
 * the first inaccessible id (or on any failure of the check itself) — never
 * which id, so the route's 400 response stays opaque.
 */
export async function validateRelatedSphereIdsAccessible(params: {
  filters: UnifiedFilters | undefined;
  /**
   * Resolves which of the given sphere ids the caller may currently `view`.
   * Must be fail-closed: on a SpiceDB/network error, return a Set smaller
   * than (never equal to or larger than) the ids that are actually
   * inaccessible — throwing is also fine, this helper treats it as "none
   * accessible".
   */
  checkViewPermission: (ids: string[]) => Promise<Set<string>>;
}): Promise<RelatedSphereAccessResult> {
  const { filters, checkViewPermission } = params;
  const ids = collectMeasureRelatedIds(filters);

  if (ids.length === 0) {
    return { valid: true };
  }

  let allowed: Set<string>;
  try {
    allowed = await checkViewPermission(ids);
  } catch {
    return { valid: false };
  }

  return { valid: ids.every((id) => allowed.has(id)) };
}

/**
 * Same rule as {@link collectMeasureRelatedIds}, generalised to an arbitrary
 * JSON value rather than a typed `UnifiedFilters`. Several save paths embed
 * one or more `UnifiedFilters` instances inside a wider, loosely-typed
 * payload the route only ever treats as `object` (a context's `definition`
 * carries one filter per level and per segment; a view's `levelFilters`
 * layer nests further still). Re-deriving each host shape to call the typed
 * collector once per embedded filter would mean the vulnerability tracks
 * every future shape change of every host; walking the raw value for the
 * `{ relationshipSystemName: 'measures_into', relatedTo: [...] }` leaf shape
 * instead makes the check correct regardless of how deep or where that leaf
 * ends up nested.
 */
function collectMeasureRelatedIdsFromAny(value: unknown, ids: Set<string> = new Set()): Set<string> {
  if (value === null || typeof value !== 'object') {
    return ids;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectMeasureRelatedIdsFromAny(item, ids));
    return ids;
  }

  const obj = value as Record<string, unknown>;
  if (obj['relationshipSystemName'] === 'measures_into' && Array.isArray(obj['relatedTo'])) {
    (obj['relatedTo'] as unknown[]).forEach((id) => {
      if (typeof id === 'string' && !isFilterVariable(id)) ids.add(id);
    });
  }

  for (const key of Object.keys(obj)) {
    collectMeasureRelatedIdsFromAny(obj[key], ids);
  }

  return ids;
}

/**
 * Validate every `measures_into` `relatedTo` id found anywhere inside an
 * arbitrary payload (not just a top-level `UnifiedFilters`). Use this at a
 * save path whose filters are embedded in a wider, loosely-typed structure —
 * e.g. a context's `applicability` + `definition` (one filter per level and
 * segment), a view's `levelFilters`, a status factor's `monitoringRules`, or
 * a column-filter's saved config. Same fail-closed contract as
 * {@link validateRelatedSphereIdsAccessible}.
 */
export async function validateRelatedSphereIdsAccessibleInAny(params: {
  value: unknown;
  checkViewPermission: (ids: string[]) => Promise<Set<string>>;
}): Promise<RelatedSphereAccessResult> {
  const { value, checkViewPermission } = params;
  const ids = [...collectMeasureRelatedIdsFromAny(value)];

  if (ids.length === 0) {
    return { valid: true };
  }

  let allowed: Set<string>;
  try {
    allowed = await checkViewPermission(ids);
  } catch {
    return { valid: false };
  }

  return { valid: ids.every((id) => allowed.has(id)) };
}
